import { useState, useMemo, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { formatDateDDMMYYYY, formatCurrency, cn } from "@/lib/utils";
import { TransactionTypeBadge } from "@/components/transactions/transaction-type-badge";
import { TransactionStatusBadge } from "@/components/transactions/transaction-status-badge";
import { Badge } from "@/components/ui/badge";
import type { Transaction, ColumnVisibility } from "@/types/transaction";

type SortColumn = keyof Transaction | null;
type SortDirection = "asc" | "desc";

interface TransactionTableProps {
    transactions: Transaction[];
    columnVisibility: ColumnVisibility;
    onViewTransaction?: (transaction: Transaction) => void;
    showCustomerColumn?: boolean;
    showCompanyIdColumn?: boolean;
    enableSelection?: boolean;
    selectedRows?: Set<number>;
    onSelectRow?: (id: number, checked: boolean) => void;
    onSelectAll?: (checked: boolean) => void;
    currentPage?: number;
    pageSize?: number;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
    totalItems?: number;
    onSortChange?: (sortBy: string, sortOrder: "asc" | "desc") => void;
}

export function TransactionTable({
    transactions,
    columnVisibility,
    onViewTransaction,
    showCustomerColumn = true,
    showCompanyIdColumn = false,
    enableSelection = false,
    selectedRows = new Set(),
    onSelectRow,
    onSelectAll,
    currentPage = 1,
    pageSize = 25,
    onPageChange,
    onPageSizeChange,
    totalItems,
    onSortChange,
}: TransactionTableProps) {
    const navigate = useNavigate();
    const [sortColumn, setSortColumn] = useState<SortColumn>("transactionDate");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

    // When totalItems is provided, data is already server-side paginated
    const isServerPaginated = totalItems !== undefined;

    // Sort transactions — skip client-side sorting when server-paginated (server handles sort)
    const sortedTransactions = useMemo(() => {
        if (isServerPaginated) return transactions;
        if (!sortColumn) return transactions;

        return [...transactions].sort((a, b) => {
            const aValue = a[sortColumn];
            const bValue = b[sortColumn];

            if (aValue === undefined || aValue === null) return 1;
            if (bValue === undefined || bValue === null) return -1;

            let comparison = 0;
            if (typeof aValue === "string" && typeof bValue === "string") {
                comparison = aValue.localeCompare(bValue);
            } else if (typeof aValue === "number" && typeof bValue === "number") {
                comparison = aValue - bValue;
            }

            return sortDirection === "asc" ? comparison : -comparison;
        });
    }, [transactions, sortColumn, sortDirection, isServerPaginated]);

    // Paginate
    const paginatedTransactions = useMemo(() => {
        if (isServerPaginated) return sortedTransactions;
        const startIndex = (currentPage - 1) * pageSize;
        return sortedTransactions.slice(startIndex, startIndex + pageSize);
    }, [sortedTransactions, currentPage, pageSize, isServerPaginated]);

    const effectiveTotalItems = isServerPaginated ? totalItems : sortedTransactions.length;
    const totalPages = Math.ceil(effectiveTotalItems / pageSize);

    const handleSort = (column: SortColumn) => {
        let newDirection: SortDirection;
        if (sortColumn === column) {
            newDirection = sortDirection === "asc" ? "desc" : "asc";
        } else {
            newDirection = "asc";
        }
        setSortColumn(column);
        setSortDirection(newDirection);
        if (isServerPaginated && onSortChange && column) {
            onSortChange(column, newDirection);
        }
    };

    const handleSelectAll = (checked: boolean | "indeterminate") => {
        if (onSelectAll && checked === true) {
            onSelectAll(true);
        } else if (onSelectAll) {
            onSelectAll(false);
        }
    };

    const handleSelectRow = (id: number, checked: boolean | "indeterminate") => {
        if (onSelectRow && checked === true) {
            onSelectRow(id, true);
        } else if (onSelectRow) {
            onSelectRow(id, false);
        }
    };

    const renderSortIcon = (column: SortColumn) => {
        if (sortColumn !== column) return null;
        return sortDirection === "asc" ? (
            <ChevronUp className="h-4 w-4" />
        ) : (
            <ChevronDown className="h-4 w-4" />
        );
    };

    const renderSortableHeader = (column: keyof Transaction, label: string, visible: boolean, className?: string) => {
        if (!visible) return null;
        return (
            <TableHead
                className={cn("cursor-pointer", className)}
                onClick={() => handleSort(column)}
            >
                <div className={cn("flex items-center gap-1", className?.includes("text-right") && "justify-end")}>
                    {label}
                    {renderSortIcon(column)}
                </div>
            </TableHead>
        );
    };

    const renderTransactionRow = (transaction: Transaction): ReactNode => (
        <TableRow key={transaction.id}>
            {enableSelection && (
                <TableCell>
                    <Checkbox
                        checked={selectedRows.has(transaction.id)}
                        onCheckedChange={(checked) =>
                            handleSelectRow(transaction.id, checked)
                        }
                    />
                </TableCell>
            )}
            {columnVisibility.referenceNumber && (
                <TableCell>
                    <button
                        onClick={() => onViewTransaction?.(transaction)}
                        className="text-primary hover:underline font-medium"
                    >
                        {transaction.referenceNumber}
                    </button>
                </TableCell>
            )}
            {columnVisibility.transactionDate && (
                <TableCell>{formatDateDDMMYYYY(transaction.transactionDate)}</TableCell>
            )}
            {columnVisibility.dueDate && (
                <TableCell>
                    {transaction.dueDate ? formatDateDDMMYYYY(transaction.dueDate) : "-"}
                </TableCell>
            )}
            {showCustomerColumn && columnVisibility.customerName && (
                <TableCell>
                    <button
                        onClick={() => navigate(`/customers/${transaction.customerId}`)}
                        className="text-primary hover:underline font-medium transition-colors"
                        title={`View details for ${transaction.customerName}`}
                    >
                        {transaction.customerName}
                    </button>
                </TableCell>
            )}
            {showCompanyIdColumn && (
                <TableCell>{`COMP-${Math.floor(Math.random() * 3) + 1}`}</TableCell>
            )}
            {columnVisibility.transactionType && (
                <TableCell>
                    <TransactionTypeBadge type={transaction.transactionType} />
                </TableCell>
            )}
            {columnVisibility.currency && (
                <TableCell>{transaction.currency}</TableCell>
            )}
            {columnVisibility.amount && (
                <TableCell className="text-right font-bold">
                    {formatCurrency(transaction.amount, transaction.currency)}
                </TableCell>
            )}
            {columnVisibility.outstandingAmount && (
                <TableCell className="text-right font-medium">
                    {transaction.outstandingAmount !== undefined
                        ? formatCurrency(transaction.outstandingAmount, transaction.currency)
                        : "-"}
                </TableCell>
            )}
            {columnVisibility.status && (
                <TableCell>
                    <TransactionStatusBadge status={transaction.status} />
                </TableCell>
            )}
            {columnVisibility.description && (
                <TableCell className="max-w-[300px] truncate">
                    {transaction.description}
                </TableCell>
            )}
            {columnVisibility.createdBy && (
                <TableCell>{transaction.createdBy}</TableCell>
            )}
            {columnVisibility.modifiedBy && (
                <TableCell>{transaction.modifiedBy || "-"}</TableCell>
            )}
            {columnVisibility.accountCode && (
                <TableCell>{transaction.accountCode || "-"}</TableCell>
            )}
            {columnVisibility.netAmount && (
                <TableCell className="text-right">
                    {transaction.netAmount !== undefined
                        ? formatCurrency(transaction.netAmount, transaction.currency)
                        : "-"}
                </TableCell>
            )}
            {columnVisibility.vatAmount && (
                <TableCell className="text-right">
                    {transaction.vatAmount !== undefined
                        ? formatCurrency(transaction.vatAmount, transaction.currency)
                        : "-"}
                </TableCell>
            )}
            {columnVisibility.onDispute && (
                <TableCell>
                    {transaction.onDispute ? (
                        <Badge variant="destructive" className="text-xs">
                            Yes
                        </Badge>
                    ) : (
                        <span className="text-muted-foreground">No</span>
                    )}
                </TableCell>
            )}
            {columnVisibility.subLedger && (
                <TableCell>{transaction.subLedger || "-"}</TableCell>
            )}
            {columnVisibility.orderNumber && (
                <TableCell>{transaction.orderNumber || "-"}</TableCell>
            )}
        </TableRow>
    );

    return (
        <>
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {enableSelection && (
                                <TableHead className="w-12">
                                    <Checkbox
                                        checked={
                                            paginatedTransactions.length > 0 &&
                                            paginatedTransactions.every((t) => selectedRows.has(t.id))
                                        }
                                        onCheckedChange={handleSelectAll}
                                    />
                                </TableHead>
                            )}
                            {renderSortableHeader("referenceNumber", "Reference Number", columnVisibility.referenceNumber)}
                            {renderSortableHeader("transactionDate", "Transaction Date", columnVisibility.transactionDate)}
                            {renderSortableHeader("dueDate", "Due Date", columnVisibility.dueDate)}
                            {renderSortableHeader("customerName", "Customer Name", showCustomerColumn && columnVisibility.customerName)}
                            {showCompanyIdColumn && (
                                <TableHead>Company ID</TableHead>
                            )}
                            {renderSortableHeader("transactionType", "Type", columnVisibility.transactionType)}
                            {renderSortableHeader("currency", "Currency", columnVisibility.currency)}
                            {renderSortableHeader("amount", "Gross", columnVisibility.amount, "text-right")}
                            {renderSortableHeader("outstandingAmount", "Outstanding", columnVisibility.outstandingAmount, "text-right")}
                            {renderSortableHeader("status", "Status", columnVisibility.status)}
                            {columnVisibility.description && <TableHead>Description</TableHead>}
                            {columnVisibility.createdBy && <TableHead>Created By</TableHead>}
                            {columnVisibility.modifiedBy && <TableHead>Modified By</TableHead>}
                            {renderSortableHeader("accountCode", "Account", columnVisibility.accountCode)}
                            {renderSortableHeader("netAmount", "Net", columnVisibility.netAmount, "text-right")}
                            {renderSortableHeader("vatAmount", "Tax", columnVisibility.vatAmount, "text-right")}
                            {renderSortableHeader("onDispute", "On Dispute", columnVisibility.onDispute)}
                            {renderSortableHeader("subLedger", "Sub Ledger", columnVisibility.subLedger)}
                            {renderSortableHeader("orderNumber", "Order Number", columnVisibility.orderNumber)}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginatedTransactions.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={
                                        Object.values(columnVisibility).filter(Boolean).length +
                                        (enableSelection ? 1 : 0)
                                    }
                                    className="h-24 text-center"
                                >
                                    No transactions found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedTransactions.map(renderTransactionRow)
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            {paginatedTransactions.length > 0 && (
                <div className="flex items-center justify-between px-2 py-4">
                    <div className="text-sm text-muted-foreground">
                        Showing {(currentPage - 1) * pageSize + 1} to{" "}
                        {Math.min(currentPage * pageSize, effectiveTotalItems)} of{" "}
                        {effectiveTotalItems} results
                    </div>
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Rows per page:</span>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-8 gap-1">
                                        {pageSize}
                                        <ChevronDown className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    {[10, 25, 50, 100].map((size) => (
                                        <DropdownMenuItem
                                            key={size}
                                            onClick={() => {
                                                onPageSizeChange?.(size);
                                                onPageChange?.(1);
                                            }}
                                        >
                                            {size}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">
                                Page {currentPage} of {totalPages}
                            </span>
                            <div className="flex gap-1">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => onPageChange?.(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => onPageChange?.(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
